/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
document.addEventListener('DOMContentLoaded', async function() {
  
  const collectionBanner2ResizeObserver = new ResizeObserver(entries => {
    for (let entry of entries) {
      const { height } = entry.target.getBoundingClientRect();
      const bannerMediaHeight = document.querySelector('.collection-banner2 .collection-banner__media').clientHeight - 100;
      if (bannerMediaHeight < height) {
        document.querySelector('.collection-banner2 .collection-banner__content').style.paddingTop = `${parseInt(height) - parseInt(bannerMediaHeight)}px`;
      } else {
        document.querySelector('.collection-banner2 .collection-banner__content').style.paddingTop = `0px`;
      }
    }
  });
  if (document.querySelector('.collection-banner2 .collection-banner__description') != null) {
    collectionBanner2ResizeObserver.observe(document.querySelector('.collection-banner2 .collection-banner__description')); 
  }
  
  document.querySelectorAll('.header-icon-bar__container').forEach(iconBar => {
    iconBar.addEventListener('click', function(e) {
      iconBar.classList.toggle('active');
    });
  });
  document.querySelectorAll('.internal-video__play').forEach(videoPlayBtn => {
    videoPlayBtn.addEventListener('click', function() {
      const video = videoPlayBtn.previousElementSibling;
      if (!video.paused) {
        video.pause();
      } else {
        video.play();
      }
      videoPlayBtn.querySelectorAll('.icon').forEach(playIcon => {
        playIcon.classList.toggle('hidden');
      });
    });
  });
  document.querySelectorAll('.read-more-container').forEach(container => {
    const text = container.querySelector('.read-more-text');
    const btn = container.querySelector('.read-more-btn');
  
    // Create a temporary clone to measure actual height
    const clone = text.cloneNode(true);
    clone.style.visibility = 'hidden';
    clone.style.position = 'absolute';
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.webkitLineClamp = 'unset';
    document.body.appendChild(clone);
  
    const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
    const lines = Math.round(clone.offsetHeight / lineHeight);
  
    document.body.removeChild(clone);
  
    if (lines > 3) {
      btn.style.display = 'inline-block';
  
      btn.addEventListener('click', () => {
        text.classList.toggle('expanded');
        btn.textContent = text.classList.contains('expanded') ? 'Read Less...' : 'Read More...';
      });
    }
  });

  document.addEventListener('click', async e => {
    if (e.target.classList.contains('prouct-complete_add')) {

      const addToCartBtn = e.target;
      const productId = e.target.dataset.id;
      addToCartBtn.classList.toggle('is-adding');
      addToCartBtn.querySelector('svg').classList.toggle('spin');
      console.log(productId);

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // formData.append('sections_url', window.location.pathname);
      // formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: productId, quantity: 1, section_url: window.location.pathname, sections: sections })
      };
      
      const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
      if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
      const oldCartData = await oldCartResponse.json();
  
      const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
      const data = await response.json();
      
      setTimeout(() => {
        // Update cart drawer contents.
        if (cartDrawer) {
          cartDrawer.renderContents(
            data,
            theme.settings.afterAtc === 'drawer'
          );
        } else if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            if (cartItems.dataset.empty === 'true') {
              window.location.reload();
            } else {
              cartItems.refresh();
            }
          }
        }
        addToCartBtn.classList.toggle('is-adding');
        addToCartBtn.querySelector('svg').classList.toggle('spin');
      }, 700);
    }
  });
});





var __fastOptions = __fastOptions || {};
// Custom missing image support (requires hook below)
__fastOptions.custom_missing_product_img = "https://baths-of-america.myshopify.com/cdn/shop/t/12/assets/coming-soon.jpg";
__fastOptions.disable_shadow_root = true;
//__fastOptions.disable_pdp_redirect=true;
__fastOptions.full_product_url_path_redirect = false;


// FastSimon product grid update
// function hooks() {
//   window.SerpOptions.registerHook('serp-product-grid', ({ products, element }) => {
//     for (const productEl of element.querySelectorAll('.fs-results-product-card')) {
//       const img = productEl.querySelector('img');
//       if (img && (!img.src || img.src.includes('no-image'))) {
//         img.src = __fastOptions.custom_missing_product_img;
//       }

//       // Get product card vendor
//       const vendor = productEl.querySelector('.vendor').textContent.toLowerCase();
//       // Hide prices based on vendor wolf
//       if (vendor == 'wolf' || vendor === 'subzero' || vendor === 'rocky mountain hardware' || vendor === 'scotsman') {
//         productEl.querySelector('.price-container')?.remove();
//         productEl.querySelector('.compare-container')?.remove();
//       }
//       // Get product card product id
//       const productId = productEl.dataset.productId;
//       // Hide prices based on tag quote
//       if (products[productId].tags.includes('quote')) {
//         productEl.querySelector('.price-container')?.remove();
//         productEl.querySelector('.compare-container')?.remove();
//       }
//     }
//   });
// }

                // productElement.querySelectorAll(".color-swatch:not(.cstm-event)").forEach((swatch) => {
                //     swatch.classList.add("cstm-event");
                //     const colorContainer = swatch.closest(".color-swatch-container");
                //     const colorName = colorContainer.querySelector(".color-swatch-color-name")?.innerHTML;
                //     if (colorName) {
                //         const swatchProductData = data.alternativeProducts.find(
                //             altProduct => altProduct["color"].trim().toLowerCase() == colorName.trim().toLowerCase()
                //         );
                //         if (swatchProductData && originalImg) {
                //             swatch.addEventListener('click', () => {
                //                 const secondImage = getSecondProductImage(swatchProductData.attributes);
                //                 const firstImage = originalImg.src;
                //                 if (secondImage && firstImage) {
                //                     imageContainer.querySelector('img.cloned.first').src = secondImage;
                //                     imageContainer.querySelector('img.cloned.second').src = firstImage;
                //                 }
                //             });
                //             colorContainer.addEventListener('mouseover', () => {
                //                 swatch.click();
                //             });
                //         }
                //     }
                // });

function hooks() {
  window.SerpOptions.registerHook('serp-product-grid', ({ products, element }) => {
    for (const productEl of element.querySelectorAll('.fs-results-product-card')) {
      
      const vendor = productEl.querySelector('.vendor')?.textContent?.toLowerCase() || '';
      const productId = productEl.dataset.productId;
      const productData = products[productId];

      const productImage = productEl.querySelector('.product-image'); 
      //coming soon image
      if (productImage &&(!productImage.src || productImage.src.includes('noImage'))) {

        productImage.style.display = 'none';

        if (!productEl.querySelector('.custom-missing-image')) {
          const newImg = document.createElement('img');
          newImg.className = 'product-image custom-missing-image';
          newImg.src = __fastOptions.custom_missing_product_img;
          newImg.alt = productImage.alt || '';

          if (productImage.loading) {
            newImg.loading = productImage.loading;
          }

          productImage.parentElement.appendChild(newImg);
        }
      }
      // Conditionally hide prices
      const shouldHidePrice = vendor === 'wolf' || vendor === 'subzero' ||
                              vendor === 'rocky mountain hardware' ||
                              vendor === 'scotsman' ||
                              productData?.tags?.includes('quote');

      if (shouldHidePrice) {
        const price = productEl.querySelector('.price-container');
        const compare = productEl.querySelector('.compare-container');

        if (price) price.classList.add('hide-price');
        if (compare) compare.classList.add('hide-price');
      }
    }
  });

  window.SerpOptions.registerHook('serp-product-swatches', ({ colors, swatchClick, showMoreClick, element }) => {

      const productCard = element.closest('.fs-results-product-card');
      const imageWrapper = productCard.querySelector(".fs-product-image-link");
      const titleContainer = productCard.querySelector(".title-container");
      const swatchContainers = productCard.querySelectorAll('.color-swatch-container');
       // Handle click for image and title
      if (swatchContainers.length > 0){
        imageWrapper.querySelector('.img-container').addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if(productCard.querySelector('.product-swatch.product-swatch-selected')){
            const variant = productCard.getAttribute("data-product-id");
            if(variant)
            {
              window.location.href = titleContainer.getAttribute('href') + "?variant=" + variant;  
              return;  
            }
          }
          window.location.href =  titleContainer.getAttribute('href');
        });

        productCard.querySelector('.title-wrapper').addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if(productCard.querySelector('.product-swatch.product-swatch-selected')){
            const variant = productCard.getAttribute("data-product-id");
            if(variant)
            {
              window.location.href = titleContainer.getAttribute('href') + "?variant=" + variant;  
              return;  
            }
          }
          window.location.href =  titleContainer.getAttribute('href');
        });
      }

  });

  window.SerpOptions.registerHook('serp-removable-tags', ({ tags, element }) => {
    // Replace "USD" with "$" in removable tag labels
    element.querySelectorAll('.removable-tag-text').forEach((tagLabel) => {
      tagLabel.textContent = tagLabel.textContent.replace('USD:', '$');
    });
  });
}


if (window.SerpOptions) {
    hooks();
} else {
    window.addEventListener('fast-serp-ready', function () {
        hooks();
    });
}


function autocompleteHooks() {    
  FastAutocomplete.registerHook('render-autocomplete-product-item', ({ product, index, element }) => {

    // Fix broken images
    const img = element.querySelector('img');
    if (img && (!img.src || img.src.includes('no-image'))) {
      img.src = __fastOptions.custom_missing_product_img;
    }

    console.log("AC product:", product);

    // Hide prices if tag contains "quote"
    const productTags = product.att?.find(a => a[0] === 'Tag')?.[1] || [];
    if (productTags.includes('quote')) {
      element.querySelector('.fs-product-price-wrapper')?.remove();
    }

    // Get Vendor from attributes
    const vendor = product.att?.find(a => a[0] === 'Vendor')?.[1]?.[0] || null;

    if (vendor) {
      const titleContainer = element.querySelector('.fs-product-title');

      if (titleContainer && !element.querySelector('.fs-product-vendor')) {
        const vendorEl = document.createElement('div');
        vendorEl.className = 'fs-product-vendor';
        vendorEl.style.fontSize = '12px';
        vendorEl.style.color = '#555';
        vendorEl.style.marginBottom = '4px';
        vendorEl.textContent = vendor;

        // Insert vendor ABOVE the title
        titleContainer.insertAdjacentElement('beforebegin', vendorEl);
      }
    }

  }); 
} 

if (window.FastAutocomplete) {
    autocompleteHooks();
} else {
    window.addEventListener('fast-autocomplete-ready', function () {
        autocompleteHooks();
    });
}
/* === BOA sticky announcement stack (2026-08-30) ==========================
   Pairs with the custom.css block of the same name. Measures the two bars
   above the header (free-shipping icon bar + announcement/utility bar) and
   publishes their heights as CSS vars. Measured live because the shipping
   text wraps on small screens and its disclosure panel changes the height.
   Safe on pages without the bars (promo landing pages): does nothing. */
(function () {
  var iconBar = document.querySelector('.header-icon-bar');
  var annBar = document.querySelector('announcement-bar');
  var iconSection = iconBar && iconBar.closest('.shopify-section');
  var annSection = annBar && annBar.closest('.shopify-section');
  if (!iconSection && !annSection) return;

  function boaMeasureBars() {
    var h1 = iconSection ? iconSection.offsetHeight : 0;
    var h2 = annSection ? annSection.offsetHeight : 0;
    var root = document.documentElement.style;
    root.setProperty('--boa-bar1-h', h1 + 'px');
    root.setProperty('--boa-bars-h', (h1 + h2) + 'px');
  }

  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(boaMeasureBars);
    if (iconSection) ro.observe(iconSection);
    if (annSection) ro.observe(annSection);
  }
  window.addEventListener('resize', boaMeasureBars);
  boaMeasureBars();
})();

;/* ------------------------------------------------------------------
   Leading semicolon is deliberate. This block is appended after other
   IIFEs in custom.js and Shopify minifies the file to one line, so if a
   block above ever ends without its own semicolon the two would fuse and
   break. It costs nothing and it was in the previous version too.

   Google Ads lead conversion | AW-963393067 "Submit Lead Form"

   THE SITE-WIDE DEFAULT COUNTER. Counts every Globo PFB submit anywhere on
   bathsofamerica.com — collection pages, the showroom page, one-off forms —
   against the store's Submit Lead Form action.

   PASTE TARGET: the END of assets/custom.js in the live theme, replacing the
   existing block that starts `var LABEL = "AW-963393067/...`. This file is the
   master copy; custom.js is not otherwise part of this package.

   Campaign pages override it. A `Promo · Conversion` block set to "Form
   submission" reports the same submit to a campaign action instead, and claims
   its forms in window.boaLeadClaims so this counter stands down rather than
   double-reporting. See snippets/promo-conversion.liquid.

   Re-verify after any Globo PFB app update; it depends on a third-party JS
   event and fails silently.

   Verify a live deploy by grepping the token UbmYCLWkt with byte offsets,
   never by line number — the CDN serves this file minified to one line.
   ------------------------------------------------------------------ */
(function () {
  var LABEL = 'AW-963393067/UbmYCLWkt-8cEKvssMsD';

  /* Forms that are not leads. Published as window.boaLeadSkip so
     snippets/promo-conversion.liquid honours the same list instead of keeping
     a second copy that drifts. ids MUST be quoted strings: the comparison is
     against String(formId), so a bare number never matches and the exclusion
     silently does nothing.
       162986, 162990 — showroom demo-event signups, not campaign leads. */
  var SKIP = ['162986', '162990'];
  window.boaLeadSkip = SKIP;

  window.boaFireLead = function (opts) {
    opts = opts || {};
    var key = 'boa_lead_' + (opts.txnId || opts.formId || 'x');
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
      /* Stamp read by snippets/promo-conversion.liquid, so a thank-you page
         does not count a lead this already counted on its way out of the form. */
      sessionStorage.setItem('boa_lead_recent', String(Date.now()));
    } catch (err) { /* private mode: Google's Count=One is the backstop */ }

    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }

    /* MUST precede the conversion event: both queue into dataLayer in order
       and reversing them silently drops the enhanced data. */
    if (opts.email) gtag('set', 'user_data', { email: opts.email });

    var payload = { send_to: LABEL };
    if (opts.formId) payload.form_id = String(opts.formId);
    if (opts.txnId)  payload.transaction_id = String(opts.txnId);
    gtag('event', 'conversion', payload);
  };

  document.addEventListener('globo.formbuilder.form.submitted', function (e) {
    var d  = e.detail || {};
    var id = String(d.formId || 'unknown');

    if (SKIP.indexOf(id) !== -1) return;

    /* A Promo · Conversion submit block on this page is reporting this form to
       a campaign action. Stand down: firing as well would report one submit
       twice, under two different actions. Claims are written while the page
       parses and read here on submit, so the two files cannot race. */
    var claims = window.boaLeadClaims;
    if (claims && (claims.any || (claims.ids && claims.ids[id]))) return;

    /* Structural opt-out: a form rendered inside [data-boa-no-conversion] is
       not a lead. Sections mark their own non-lead forms — first user is
       sections/showroom-events.liquid — so a new event card is excluded
       without anyone editing SKIP. Embedded forms only: popup forms mount at
       body level, outside the section, so those still need SKIP. */
    if (d.form && d.form.closest && d.form.closest('[data-boa-no-conversion]')) return;

    /* data-type, not name: PFB names fields from each form's own key labels
       (text, text-1, email, phone-1, textarea), which vary per form.
       data-type is set by field type and holds everywhere. */
    var input = d.form && d.form.querySelector('input[data-type="email"]');

    window.boaFireLead({
      formId: id,
      /* Trimmed and lowercased because Google normalises before hashing and
         expects the same. */
      email: (input && input.value) ? input.value.trim().toLowerCase() : ''
    });
  }, true);
})();
